import React, { useEffect, useRef, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Plus, Printer, Search, User, X } from "lucide-react";
import POSNegozio from "./POSNegozio";
import { getPrinters } from "@/lib/usePrecontoPrinter";
import { pickPrinter, printerUsable } from "@/lib/pickPrinter";
import { sendRawTcp } from "@/lib/printTransport";
import { buildLaundrySlip } from "@/lib/laundrySlip";
import { getActiveStoreId, useStore } from "@/lib/storeContext";

const CAT_ID = "lav-capi";

const CAPI = [
  ["Cappotto", 12],
  ["Pantalone lungo", 6],
  ["Pantalone corto", 5],
  ["Maglietta", 3],
  ["Piumone", 22],
  ["Coperta", 14],
  ["Camicia", 3.5],
  ["Camicia di seta", 6],
  ["Blusa", 4],
  ["Polo", 3],
  ["Felpa", 5],
  ["Maglione", 6],
  ["Cardigan", 6],
  ["Giacca", 8],
  ["Giaccone", 12],
  ["Trench", 14],
  ["Piumino", 16],
  ["Gilet", 5],
  ["Jeans", 5],
  ["Gonna corta", 5],
  ["Gonna lunga", 6.5],
  ["Abito uomo", 12],
  ["Abito donna", 14],
  ["Abito da sposa", 80],
  ["Smoking", 18],
  ["Completo", 15],
  ["Vestito bambino", 6],
  ["Tuta", 7],
  ["Shorts", 4],
  ["Cravatta", 3],
  ["Sciarpa", 4],
  ["Cappello", 4],
  ["Guanti", 3],
  ["Calze", 2],
  ["Intimo", 2.5],
  ["Pigiama", 5],
  ["Accappatoio", 8],
  ["Asciugamano", 3],
  ["Telo bagno", 4],
  ["Lenzuola singole", 8],
  ["Lenzuola matrimoniali", 12],
  ["Federa", 2.5],
  ["Copripiumino", 14],
  ["Trapunta", 18],
  ["Plaid", 8],
  ["Tovaglia", 6],
  ["Tovaglioli", 3],
  ["Tenda", 8],
  ["Tappeto piccolo", 15],
  ["Peluche", 6],
  ["Camice", 5],
  ["Divisa", 7],
  ["Copridivano", 18],
  ["Smacchiatura", 8],
  ["Stiratura", 3],
  ["Lavaggio express", 5],
];

const FALLBACK_CATALOG = {
  cats: [{ id: CAT_ID, name: "Capi", color: "#1E2D5B", visible: true, mode: "lavanderia" }],
  prods: CAPI.map(([name, price], i) => ({
    id: `lav-capo-${i}`,
    name,
    price,
    vat: 22,
    category_id: CAT_ID,
    show_price: true,
    visible: true,
    mode: "lavanderia",
  })),
};

const STATUS = {
  received: "Ricevuto",
  washing: "In lavaggio",
  ready: "Pronto",
  delivered: "Consegnato",
};

const SCOPES = [
  ["open", "Aperti"],
  ["today", "Oggi"],
  ["ready", "Pronti"],
  ["delivered", "Consegnati"],
];

const pickupLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function POSLavanderia() {
  const inner = useRef(null);
  const ticketRef = useRef(null);
  const { storeId } = useStore();
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [scope, setScope] = useState("open");
  const [search, setSearch] = useState("");
  const [scan, setScan] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", pickup: "", note: "" });

  useEffect(() => { ticketRef.current = ticket; }, [ticket]);

  const load = () => {
    const params = { scope };
    const sid = getActiveStoreId();
    if (sid) params.store_id = sid;
    return api.get("/laundry/tickets", { params })
      .then((r) => setTickets(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTickets([]));
  };
  useEffect(() => { load(); }, [scope, storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fillCart = (doc) => {
    const rows = Array.isArray(doc?.items) ? doc.items : [];
    inner.current?.setLines(rows.map((it) => ({
      product_id: it.product_id || null,
      name: it.description,
      qty: Number(it.qty) || 1,
      price: Number(it.price) || 0,
      vat: it.vat ?? 22,
      note: it.notes || "",
      laundry_item_id: it.id,
    })));
  };

  const applyTicket = (doc) => {
    setTicket(doc);
    setForm({
      name: doc?.customer_name || "",
      phone: doc?.phone || "",
      pickup: toLocalInput(doc?.pickup_at),
      note: doc?.note || "",
    });
    fillCart(doc);
    setDraftLines(Array.isArray(doc?.items) ? doc.items : []);
  };

  const clearForm = () => {
    setTicket(null);
    setForm({ name: "", phone: "", pickup: "", note: "" });
    setDraftLines([]);
    setShowNotes(false);
    inner.current?.setLines([]);
  };

  const openTicket = async (row) => {
    try {
      const r = await api.get("/laundry/lookup", { params: { code: row.number || row.barcode } });
      applyTicket(r.data);
    } catch {
      applyTicket(row);
    }
  };

  const linesPayload = () => (inner.current?.getLines?.() || []).map((l) => ({
    product_id: l.product_id || null,
    name: l.name,
    qty: Number(l.qty) || 1,
    price: Number(l.price) || 0,
    vat: l.vat ?? 22,
    notes: l.note || "",
  }));

  const printSlip = async (doc) => {
    try {
      const tenant = (await api.get("/tenant/me")).data || {};
      const printers = await getPrinters();
      const target = pickPrinter(printers, "preconto", doc?.store_id || getActiveStoreId());
      if (!printerUsable(target)) {
        toast.error("Collega la stampante preconto per la bolla");
        return;
      }
      const sent = await sendRawTcp({
        host: target.ip,
        port: target.port || 9100,
        data: buildLaundrySlip(tenant, doc),
        encoding: "ISO-8859-1",
      });
      if (!sent.ok) toast.error("Bolla non stampata");
      else toast.success(`Bolla ${doc.number} su ${target.name}`);
    } catch {
      toast.error("Bolla non stampata");
    }
  };

  const saveTicket = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.trim();
    if (!name || !phone) return toast.error("Scrivi nome e telefono");
    if (!form.pickup) return toast.error("Metti data e ora di ritiro");
    const lines = linesPayload();
    if (!lines.length) return toast.error("Aggiungi i capi nel conto, poi salva");
    const editing = !!(ticket?.id && ticket.status && ticket.status !== "delivered");
    const body = {
      name,
      phone,
      pickup_at: form.pickup,
      note: form.note,
      customer_id: ticket?.customer_id || null,
      store_id: getActiveStoreId() || null,
      lines,
    };
    try {
      const r = editing
        ? await api.patch(`/laundry/tickets/${ticket.id}`, body)
        : await api.post("/laundry/tickets", body);
      const doc = r.data?.ticket || r.data;
      applyTicket(doc);
      toast.success(editing ? `Ritiro ${doc.number} aggiornato` : `Bolla ${doc.number} salvata`);
      load();
      printSlip(doc);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const startAnother = () => {
    const keep = {
      name: form.name,
      phone: form.phone,
      pickup: "",
      note: "",
      customer_id: ticket?.customer_id || null,
    };
    setTicket(keep.customer_id ? { customer_id: keep.customer_id, customer_name: keep.name, phone: keep.phone } : null);
    setForm({ name: keep.name, phone: keep.phone, pickup: "", note: "" });
    setDraftLines([]);
    inner.current?.setLines([]);
    toast.info("Nuovo ritiro: aggiungi i capi e salva. L'altro resta aperto.");
  };

  const setStatus = async (status) => {
    if (!ticket?.id) return toast.error("Apri un ritiro");
    if (status === "delivered" && !window.confirm(`Consegnare ${ticket.number}? I capi escono dal banco.`)) return;
    try {
      const r = await api.post(`/laundry/tickets/${ticket.id}/status`, { status });
      applyTicket(r.data);
      toast.success(STATUS[status] || "Stato aggiornato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stato non aggiornato");
    }
  };

  const takeDeposit = async () => {
    if (!ticket?.id) return toast.error("Apri un ritiro");
    const amount = Number(inner.current?.getKeypad?.()?.value || 0);
    if (amount <= 0) return toast.error("Scrivi l'acconto sul tastierino, poi Acconto");
    try {
      const r = await api.post(`/laundry/tickets/${ticket.id}/payment`, { amount });
      applyTicket(r.data);
      inner.current?.clearKeypad?.();
      toast.success(`Acconto ${formatEUR(amount)}. Resta ${formatEUR(r.data.balance)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Acconto non registrato");
    }
  };

  const notifyReady = async () => {
    if (!ticket?.id) return toast.error("Apri un ritiro");
    try {
      const r = await api.post(`/laundry/tickets/${ticket.id}/notify`);
      if (r.data?.ticket) applyTicket(r.data.ticket);
      if (r.data?.ok) toast.success(r.data.detail || "Avviso inviato");
      else {
        toast.info(r.data?.detail || "Apri WhatsApp");
        if (r.data?.wa_url) window.open(r.data.wa_url, "_blank", "noopener,noreferrer");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Avviso non inviato");
    }
  };

  const onScan = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = scan.trim();
    if (!code) return;
    try {
      const r = await api.get("/laundry/lookup", { params: { code } });
      applyTicket(r.data);
      setScan("");
      toast.success(r.data.number);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non trovato");
    }
  };

  const readNotes = () => {
    const lines = inner.current?.getLines?.() || [];
    setDraftLines(lines.map((l, i) => ({
      id: l.laundry_item_id || `line-${i}`,
      description: l.name,
      notes: l.note || "",
    })));
    setShowNotes(true);
  };

  const editNote = (index, notes) => {
    const lines = inner.current?.getLines?.() || [];
    inner.current?.setLines(lines.map((l, i) => (i === index ? { ...l, note: notes } : l)));
    setDraftLines((prev) => prev.map((row, i) => (i === index ? { ...row, notes } : row)));
  };

  const onPaid = async (order) => {
    const current = ticketRef.current;
    const amount = Number(order?.total);
    if (!current?.id || !order?.id || order.gestionale || !Number.isFinite(amount)) {
      load();
      return;
    }
    try {
      const r = await api.post(`/laundry/tickets/${current.id}/payment`, { amount, order_id: order.id });
      applyTicket(r.data);
      const left = Number(r.data?.balance || 0);
      const over = Number(r.data?.paid_amount || 0) > Number(r.data?.total || 0) + 0.05;
      if (over) toast.error(`Incassato ${formatEUR(r.data.paid_amount)} su un ritiro di ${formatEUR(r.data.total)}.`);
      else toast.success(left <= 0.001
        ? "Scontrino emesso. I capi restano qui finché non premi Consegnato."
        : `Incasso registrato. Resta ${formatEUR(left)}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scontrino emesso, ritiro non aggiornato");
    }
    load();
  };

  const q = search.trim().toLowerCase();
  const filtered = (Array.isArray(tickets) ? tickets : []).filter((t) => {
    if (!q) return true;
    return [t.number, t.barcode, t.customer_name, t.phone].some((v) => String(v || "").toLowerCase().includes(q));
  });

  const balance = Number(ticket?.balance ?? ticket?.total ?? 0);
  const paid = Number(ticket?.paid_amount || 0);
  const saleNote = ticket?.number
    ? `Ritiro ${ticket.number} · ${form.name || ticket.customer_name || ""}${form.phone ? ` · ${form.phone}` : ""}${form.pickup ? ` · Ritiro ${pickupLabel(form.pickup)}` : ""}`
    : (form.name ? `Cliente: ${form.name}${form.phone ? ` · ${form.phone}` : ""}` : null);

  const topStrip = (
    <div className="border-b border-velta-border bg-white px-3 py-2 flex flex-col gap-2 shrink-0" data-testid="lav-customers-strip">
      <form onSubmit={saveTicket} className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Ritiro</span>
        <input data-testid="lav-newc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome" className="h-9 w-36 bg-white border border-velta-border rounded-md px-2 text-sm outline-none focus:border-[#1E2D5B]" />
        <input data-testid="lav-newc-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono" className="h-9 w-32 bg-white border border-velta-border rounded-md px-2 text-sm outline-none focus:border-[#1E2D5B]" />
        <input data-testid="lav-pickup" required type="datetime-local" value={form.pickup} onChange={(e) => setForm({ ...form, pickup: e.target.value })} className="h-9 bg-white border border-velta-border rounded-md px-2 text-sm outline-none focus:border-[#1E2D5B]" />
        <input data-testid="lav-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note ritiro (macchia, urgenza)" className="h-9 w-52 bg-white border border-velta-border rounded-md px-2 text-sm outline-none focus:border-[#1E2D5B]" />
        <button data-testid="lav-save-ticket" className="h-9 px-3 bg-[#1E2D5B] text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> {ticket?.id && ticket.status && ticket.status !== "delivered" ? "Aggiorna ritiro" : "Salva ritiro"}
        </button>
        <button type="button" onClick={startAnother} className="h-9 px-3 border border-velta-border rounded-md text-sm">Nuovo ritiro</button>
        <button type="button" onClick={readNotes} className="h-9 px-3 border border-velta-border rounded-md text-sm">Note capi</button>
      </form>
      {showNotes && (
        <div className="flex flex-col gap-1 max-h-28 overflow-auto" data-testid="lav-line-notes">
          {draftLines.length === 0 && <span className="text-xs text-velta-mute">Nessun capo nel conto.</span>}
          {draftLines.map((row, i) => (
            <label key={row.id || i} className="flex items-center gap-2 text-xs">
              <span className="w-36 truncate">{row.description}</span>
              <input value={row.notes || ""} onChange={(e) => editNote(i, e.target.value)} placeholder="Macchia, strappo, bottone…" className="h-8 flex-1 border border-velta-border rounded-md px-2" />
            </label>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-velta-mute" />
          <input data-testid="lav-scan" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={onScan} placeholder="Spara bolla o etichetta" className="h-9 w-52 pl-7 bg-velta-bg border border-velta-border rounded-md text-sm outline-none focus:border-[#1E2D5B]" />
        </div>
        <input data-testid="lav-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, telefono, numero" className="h-9 w-44 bg-white border border-velta-border rounded-md px-2 text-sm outline-none focus:border-[#1E2D5B]" />
        {SCOPES.map(([id, label]) => (
          <button key={id} type="button" onClick={() => setScope(id)} className={`h-9 px-3 rounded-md text-sm border ${scope === id ? "bg-[#1E2D5B] text-white border-[#1E2D5B]" : "bg-white border-velta-border"}`}>{label}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 min-w-0 overflow-x-auto pos-scroll">
        {filtered.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`lav-ticket-${t.id}`}
            onClick={() => openTicket(t)}
            className={`shrink-0 h-9 px-3 rounded-md border text-left text-sm ${ticket?.id === t.id ? "border-velta-ink bg-velta-bg" : "border-velta-border bg-white hover:border-velta-ink"} ${t.status === "ready" ? "border-[#2F9E77]" : ""}`}
          >
            <span className="font-medium">{t.number}</span>
            <span> · {t.customer_name}</span>
            <span className="text-velta-mute"> · {STATUS[t.status] || t.status}</span>
            {t.pickup_at ? <span className="text-[#1E2D5B]"> · {pickupLabel(t.pickup_at)}</span> : null}
          </button>
        ))}
        {filtered.length === 0 && <span className="text-xs text-velta-mute">Nessun ritiro</span>}
      </div>
      {ticket?.id && (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(STATUS).map(([id, label]) => (
            <button key={id} type="button" onClick={() => setStatus(id)} className={`h-8 px-2 rounded-md text-xs border ${ticket.status === id ? "bg-[#1E2D5B] text-white border-[#1E2D5B]" : "bg-white border-velta-border"}`}>{label}</button>
          ))}
          <button type="button" onClick={takeDeposit} className="h-8 px-2 rounded-md text-xs border border-velta-border">Acconto</button>
          <button type="button" onClick={notifyReady} className="h-8 px-2 rounded-md text-xs border border-velta-border inline-flex items-center gap-1"><Bell className="w-3 h-3" /> Avvisa pronto</button>
          <button type="button" onClick={() => printSlip(ticket)} className="h-8 px-2 rounded-md text-xs border border-velta-border inline-flex items-center gap-1"><Printer className="w-3 h-3" /> Stampa bolla</button>
          <span className="text-xs font-medium">{ticket.number} · pagato {formatEUR(paid)} · resta {formatEUR(balance)}</span>
        </div>
      )}
    </div>
  );

  const cartHeaderExtra = (form.name || ticket?.number) ? (
    <div className="px-3 py-2 border-b border-velta-border text-[11px] bg-velta-bg/60 shrink-0 flex items-center justify-between gap-2" data-testid="lav-linked-customer">
      <div className="font-medium flex items-center gap-1 min-w-0">
        <User className="w-3 h-3 shrink-0" />
        <span className="truncate">
          {ticket?.number ? `${ticket.number} · ` : ""}{form.name}{form.phone ? ` · ${form.phone}` : ""}{form.pickup ? ` · Ritiro ${pickupLabel(form.pickup)}` : ""}
          {ticket?.id ? ` · ${balance <= 0.001 && paid > 0 ? "Saldato, ancora da consegnare" : `resta ${formatEUR(balance)}`}` : ""}
        </span>
      </div>
      <button type="button" onClick={clearForm} className="text-velta-mute inline-flex items-center gap-1 hover:text-velta-error shrink-0" data-testid="lav-unlink-customer">
        <X className="w-3 h-3" /> Scollega
      </button>
    </div>
  ) : null;

  return (
    <POSNegozio
      mode="lavanderia"
      title="Velta One · Lavanderia"
      testidPrefix="lav"
      innerRef={inner}
      fallbackCatalog={FALLBACK_CATALOG}
      saleNote={saleNote}
      topStrip={topStrip}
      cartHeaderExtra={cartHeaderExtra}
      onAfterClose={onPaid}
      onClearAll={clearForm}
    />
  );
}
